import { useRef, type PointerEvent as ReactPointerEvent } from "react";

/**
 * Pointer-drag plumbing for the desk arrangement (whitespace/organization
 * pass, 2026-08-28). Deliberately dumb: it reports pixel deltas and a final
 * drop point, and whether the gesture ever exceeded the click threshold —
 * every decision about what a drop MEANS (reposition, open, minimize) stays
 * with the caller. Pure client presentation; nothing here touches state the
 * server owns.
 */

export interface DragEndInfo {
  /** True once the pointer travelled beyond the click threshold. */
  readonly moved: boolean;
  readonly clientX: number;
  readonly clientY: number;
  readonly dx: number;
  readonly dy: number;
}

const CLICK_THRESHOLD_PX = 5;

export function usePointerDrag(handlers: {
  readonly onStart?: () => void;
  readonly onMove?: (dx: number, dy: number, e: PointerEvent) => void;
  readonly onEnd: (info: DragEndInfo) => void;
}): (e: ReactPointerEvent<HTMLElement>) => void {
  const latest = useRef(handlers);
  latest.current = handlers;

  return (down: ReactPointerEvent<HTMLElement>) => {
    if (down.button !== 0) return;
    const startX = down.clientX;
    const startY = down.clientY;
    let moved = false;
    latest.current.onStart?.();

    const onMove = (e: PointerEvent): void => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!moved && Math.hypot(dx, dy) > CLICK_THRESHOLD_PX) moved = true;
      if (moved) latest.current.onMove?.(dx, dy, e);
    };
    const onUp = (e: PointerEvent): void => {
      globalThis.removeEventListener("pointermove", onMove);
      globalThis.removeEventListener("pointerup", onUp);
      globalThis.removeEventListener("pointercancel", onUp);
      latest.current.onEnd({ moved, clientX: e.clientX, clientY: e.clientY, dx: e.clientX - startX, dy: e.clientY - startY });
    };
    globalThis.addEventListener("pointermove", onMove);
    globalThis.addEventListener("pointerup", onUp);
    globalThis.addEventListener("pointercancel", onUp);
  };
}

/** Current root font size, for px -> rem conversions (the scale rule makes rem viewport-dependent). */
export function remBase(): number {
  const raw = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(raw) && raw > 0 ? raw : 16;
}

/** A rectangle in client px, the shape `getBoundingClientRect` returns. */
export interface ClientRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * THE WALLS (drag pass, 2026-09-09). A dragged card cannot leave the browser
 * window on the left, the right or the bottom, and cannot rise above the top
 * of the region it belongs to.
 *
 * Pure: given where the card started, the region it lives in, the viewport,
 * and the raw pointer delta, return the delta the card may actually take.
 * Applied on every move, so the card visibly halts at the wall, and again on
 * the drop, so where it rests is exactly where it stopped — the old behaviour
 * re-derived the rest from the pointer and clamped it to a smaller box, which
 * is why a card could jump to a different part of the screen the moment it was
 * let go. A card somehow larger than its room in either axis simply does not
 * move on that axis.
 *
 * Lives here rather than on a screen because two surfaces need it: the front
 * door's shelf of case covers, and any future dragged card. The Storyteller
 * play screen no longer floats documents over the narration, so its desk has
 * no walls to hold — it hit-tests the papers themselves (documents/desk.tsx).
 */
export function clampDragDelta(
  start: ClientRect,
  region: ClientRect,
  viewport: { readonly width: number; readonly height: number },
  dx: number,
  dy: number,
): { readonly x: number; readonly y: number } {
  const minDx = 0 - start.left;
  const maxDx = viewport.width - start.right;
  const minDy = region.top - start.top;
  const maxDy = Math.min(viewport.height, region.bottom) - start.bottom;
  const x = maxDx < minDx ? 0 : Math.min(Math.max(dx, minDx), maxDx);
  const y = maxDy < minDy ? 0 : Math.min(Math.max(dy, minDy), maxDy);
  return { x, y };
}
